// Imports => React
import { useEffect, useMemo } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import { TABS } from "@constants";
import { AcButton, AcIcon, ICONS, KEYS, THEMES, VARIANTS } from "@opp/ui-kit";

// Imports => Utilities
import { AcSetDocumentTitle } from "@utils";

// Imports => Molecules
import AcSettlementFilterSelectModal from "@molecules/ac-settlement-filter-modal/ac-settlement-filter-modal";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcTabContainer,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import { AcTranslateTabs } from "../../utilities/ac-translate-consts";
import {
  useQueryParams,
  withDefault,
  NumberParam,
  StringParam,
  createEnumParam,
} from "use-query-params";

const _CLASSES = {
  MAIN: "ac-page ac-settlements-overview",
};

type Filters =
  | "all"
  | "current"
  | "transfer"
  | "invoiced"
  | "paid"
  | "cancelled";

export const useSettlementsParams = () => {
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
    date: withDefault(StringParam, "01-01-2000"),
    order: withDefault(createEnumParam(["-date", "date"]), "-date"),
    filter: withDefault(
      createEnumParam([
        "all",
        "current",
        "transfer",
        "invoiced",
        "paid",
        "cancelled",
      ]),
      ""
    ),
  });
  return {
    values: {
      page: query.page,
      date: query.date,
      order: query.order,
      filter: query.filter,
    },
    operations: { setQuery },
  };
};
const AcSettlementsOverview = ({ store: { ui }, t }) => {
  const {
    values: { filter },
    operations: { setQuery },
  } = useSettlementsParams();

  useEffect(() => {
    AcSetDocumentTitle(t("TITLES.SETTLEMENTS", "Payouts", { ns: "titles" }));
  }, []);

  const changeFilters = (filters: Filters) => {
    ui.reset(KEYS.MODAL);
    setQuery({ filter: filters });
  };

  const displayFilterModal = async (event) => {
    if (event && (typeof event.cancelable !== "boolean" || event.cancelable)) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.FILTERS", "Filters", { ns: "titles" })}`,
      name: "settlement-filter-modal",
      body: (
        <AcSettlementFilterSelectModal
          filters={filter}
          callback={changeFilters}
        />
      ),
      closeable: true,
      visible: true,
      centered: true,
      overflowVisible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL);
      },
    });
  };

  const renderTitle = useMemo(() => {
    return (
      <AcHeading
        theme={THEMES.DARK}
        rank={1}
        className={"h-margin-y-0 h-margin-top-15"}
      >
        {t("TITLES.SETTLEMENTS", "Payouts", { ns: "titles" })}
      </AcHeading>
    );
  }, []);

  const renderTabs = useMemo(() => {
    let collection = TABS.SETTLEMENTS;
    collection = AcTranslateTabs(t, collection);

    let variant;
    variant = VARIANTS.GHOST;

    return (
      <AcTabContainer
        collection={collection}
        forceShowOneItem
        data={{ filters: filter }}
      >
        <AcButton
          variant={variant}
          className={"filter-button"}
          callback={displayFilterModal}
        >
          <span>{t("filter.filter", "Filter", { ns: "payouts" })}</span>
          <AcIcon icon={ICONS.TUNE} />
        </AcButton>
      </AcTabContainer>
    );
  }, [filter]);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  }, []);

  return (
    <div className={getMainClassNames}>
      <AcContainer fluid>
        <AcRow className={"h-margin-bottom-40"}>
          <AcColumn xs={12}>{renderTitle}</AcColumn>
        </AcRow>
      </AcContainer>

      {renderTabs}
    </div>
  );
};

export default withTranslation(["titles", "payouts", "tabs"])(
  withStore(observer(AcSettlementsOverview))
);
